import { ROLE_LABELS, ROLES, type Role, WEEKDAYS_PT } from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Chips,
  Loading,
  Modal,
  PageHeader,
  SelectInput,
  Tabs,
  TextInput,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { ago, formatBrPhone } from "../../lib/format";

interface TeamUser {
  id: string;
  email: string;
  name: string;
  status: "invited" | "active" | "disabled";
  roles: Role[];
  totpConfirmed: boolean;
  lastLoginAt: string | null;
}
interface SellerProfile {
  userId: string;
  whatsapp: string;
  weight: number;
  acceptingLeads: boolean;
  walletId: string | null;
  hours: { weekday: number; startMinute: number; endMinute: number }[];
}
interface TeacherProfile {
  userId: string;
  status: "onboarding" | "active" | "paused" | "disabled";
  walletId: string | null;
  companyName: string | null;
  whatsapp: string | null;
  photoUrl: string | null;
  bio: string | null;
  acceptsDuo: boolean;
  maxStudents: number;
  maxWeeklyMinutes: number;
  breakMinutes: number;
  hasCnpj: boolean;
}
interface WalletRow {
  ownerId: string;
  ownerType: string;
  walletId: string;
  status: string;
}

const STATUS_LABEL = { invited: "Convidado", active: "Ativo", disabled: "Desativado" } as const;

export function AdminTeam() {
  const users = useQuery({
    queryKey: ["team"],
    queryFn: () => api.get<{ items: TeamUser[] }>("/v1/admin/users"),
  });
  const sellers = useQuery({
    queryKey: ["sellers"],
    queryFn: () => api.get<{ items: SellerProfile[] }>("/v1/admin/sellers"),
  });
  const teachers = useQuery({
    queryKey: ["teachers"],
    queryFn: () => api.get<{ items: TeacherProfile[] }>("/v1/admin/teachers"),
  });
  const wallets = useQuery({
    queryKey: ["wallets"],
    queryFn: () => api.get<{ items: WalletRow[] }>("/v1/admin/wallets"),
  });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamUser | null>(null);
  if (users.isLoading) return <Loading />;
  if (users.error) return <Alert kind="risk">{errorMessage(users.error)}</Alert>;
  return (
    <>
      <PageHeader
        title="Equipe"
        subtitle="Convites, funções, dados da vendedora e do professor e subcontas da Asaas."
        actions={<Button onClick={() => setInviting(true)}>Convidar pessoa</Button>}
      />
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Funções</th>
              <th>Status</th>
              <th>Subconta</th>
              <th>Último acesso</th>
            </tr>
          </thead>
          <tbody>
            {users.data?.items.map((u) => {
              const w = wallets.data?.items.find((x) => x.ownerId === u.id);
              return (
                <tr key={u.id} className="clickable" onClick={() => setEditing(u)}>
                  <td>
                    <strong>{u.name}</strong>
                    <div className="small muted">{u.email}</div>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      {u.roles.map((r) => (
                        <Badge key={r} color="blue">
                          {ROLE_LABELS[r]}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Badge
                      color={
                        u.status === "active" ? "green" : u.status === "invited" ? "gold" : "gray"
                      }
                    >
                      {STATUS_LABEL[u.status]}
                    </Badge>
                    {u.roles.includes("admin") && !u.totpConfirmed ? (
                      <div className="small muted">sem autenticador</div>
                    ) : null}
                  </td>
                  <td>
                    {w ? (
                      <Badge color={w.status === "approved" ? "green" : "gold"}>{w.status}</Badge>
                    ) : (
                      <span className="muted small">sem subconta</span>
                    )}
                  </td>
                  <td className="small">{u.lastLoginAt ? ago(u.lastLoginAt) : "nunca"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {inviting ? <InviteModal onClose={() => setInviting(false)} /> : null}
      {editing ? (
        <EditUserModal
          user={editing}
          seller={sellers.data?.items.find((s) => s.userId === editing.id) ?? null}
          teacher={teachers.data?.items.find((t) => t.userId === editing.id) ?? null}
          wallet={wallets.data?.items.find((w) => w.ownerId === editing.id) ?? null}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", roles: ["seller"] as Role[] });
  const m = useMutation({
    mutationFn: () => api.post("/v1/admin/users", f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team"] });
      onClose();
    },
  });
  return (
    <Modal title="Convidar pessoa" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <TextInput
          label="Nome completo"
          required
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <TextInput
          label="E-mail"
          type="email"
          required
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
        />
        <div className="field">
          <span className="label">Funções</span>
          {ROLES.map((r) => (
            <Checkbox
              key={r}
              label={ROLE_LABELS[r]}
              checked={f.roles.includes(r)}
              onChange={(e) =>
                setF({
                  ...f,
                  roles: e.target.checked ? [...f.roles, r] : f.roles.filter((x) => x !== r),
                })
              }
            />
          ))}
        </div>
        {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
        <Button type="submit" loading={m.isPending} disabled={f.roles.length === 0}>
          Enviar convite
        </Button>
      </form>
    </Modal>
  );
}

type EditTab = "access" | "seller" | "teacher" | "wallet";

function EditUserModal({
  user,
  seller,
  teacher,
  wallet,
  onClose,
}: {
  user: TeamUser;
  seller: SellerProfile | null;
  teacher: TeacherProfile | null;
  wallet: WalletRow | null;
  onClose: () => void;
}) {
  const tabs: { value: EditTab; label: string }[] = [{ value: "access", label: "Acesso" }];
  if (user.roles.includes("seller")) tabs.push({ value: "seller", label: "Vendedora" });
  if (user.roles.includes("teacher")) tabs.push({ value: "teacher", label: "Professor" });
  if (user.roles.includes("seller") || user.roles.includes("teacher"))
    tabs.push({ value: "wallet", label: "Subconta Asaas" });
  const [tab, setTab] = useState<EditTab>("access");
  return (
    <Modal title={user.name} onClose={onClose}>
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "access" ? <AccessForm user={user} /> : null}
      {tab === "seller" ? <SellerForm userId={user.id} seller={seller} /> : null}
      {tab === "teacher" ? <TeacherForm userId={user.id} teacher={teacher} /> : null}
      {tab === "wallet" ? (
        <WalletForm
          userId={user.id}
          ownerType={user.roles.includes("teacher") ? "teacher" : "seller"}
          wallet={wallet}
        />
      ) : null}
    </Modal>
  );
}

function AccessForm({ user }: { user: TeamUser }) {
  const qc = useQueryClient();
  const [roles, setRoles] = useState<Role[]>(user.roles);
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/v1/admin/users/${user.id}`, { ...body, reason: reason || undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });
  return (
    <div className="stack">
      <div className="field">
        <span className="label">Funções</span>
        {ROLES.map((r) => (
          <Checkbox
            key={r}
            label={ROLE_LABELS[r]}
            checked={roles.includes(r)}
            onChange={(e) =>
              setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))
            }
          />
        ))}
      </div>
      <TextInput
        label="Motivo (fica na auditoria)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
      {m.isSuccess ? (
        <Alert kind="success">Alterações salvas. As sessões da pessoa foram encerradas.</Alert>
      ) : null}
      <div className="row">
        <Button loading={m.isPending} onClick={() => m.mutate({ roles })}>
          Salvar funções
        </Button>
        {user.status !== "disabled" ? (
          <Button variant="danger" onClick={() => m.mutate({ status: "disabled" })}>
            Desativar
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => m.mutate({ status: "active" })}>
            Reativar
          </Button>
        )}
        {user.totpConfirmed ? (
          <Button variant="ghost" onClick={() => m.mutate({ resetTotp: true })}>
            Zerar autenticador
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function SellerForm({ userId, seller }: { userId: string; seller: SellerProfile | null }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    whatsapp: seller?.whatsapp ? formatBrPhone(seller.whatsapp) : "",
    weight: String(seller?.weight ?? 1),
    acceptingLeads: seller?.acceptingLeads ?? true,
    start: seller?.hours[0] ? String(seller.hours[0].startMinute / 60) : "9",
    end: seller?.hours[0] ? String(seller.hours[0].endMinute / 60) : "18",
    days: seller?.hours.length ? [...new Set(seller.hours.map((h) => h.weekday))] : [1, 2, 3, 4, 5],
  });
  const m = useMutation({
    mutationFn: () =>
      api.put(`/v1/admin/users/${userId}/seller-profile`, {
        whatsapp: f.whatsapp,
        weight: Number(f.weight),
        acceptingLeads: f.acceptingLeads,
        hours: f.days.map((weekday) => ({
          weekday,
          startMinute: Number(f.start) * 60,
          endMinute: Number(f.end) * 60,
        })),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sellers"] }),
  });
  return (
    <div className="stack">
      <TextInput
        label="WhatsApp comercial"
        value={f.whatsapp}
        onChange={(e) => setF({ ...f, whatsapp: e.target.value })}
      />
      <div className="grid cols-3">
        <TextInput
          label="Peso no rodízio"
          type="number"
          min={0}
          max={10}
          value={f.weight}
          onChange={(e) => setF({ ...f, weight: e.target.value })}
        />
        <TextInput
          label="Início (hora)"
          type="number"
          min={0}
          max={23}
          value={f.start}
          onChange={(e) => setF({ ...f, start: e.target.value })}
        />
        <TextInput
          label="Fim (hora)"
          type="number"
          min={1}
          max={24}
          value={f.end}
          onChange={(e) => setF({ ...f, end: e.target.value })}
        />
      </div>
      <div className="chips">
        {[1, 2, 3, 4, 5, 6, 0].map((w) => (
          <button
            key={w}
            type="button"
            className={`chip${f.days.includes(w) ? " active" : ""}`}
            onClick={() =>
              setF({
                ...f,
                days: f.days.includes(w) ? f.days.filter((d) => d !== w) : [...f.days, w],
              })
            }
          >
            {WEEKDAYS_PT[w]}
          </button>
        ))}
      </div>
      <Checkbox
        label="Recebendo leads"
        checked={f.acceptingLeads}
        onChange={(e) => setF({ ...f, acceptingLeads: e.target.checked })}
      />
      {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
      {m.isSuccess ? <Alert kind="success">Dados da vendedora salvos.</Alert> : null}
      <Button loading={m.isPending} onClick={() => m.mutate()}>
        Salvar
      </Button>
    </div>
  );
}

function TeacherForm({ userId, teacher }: { userId: string; teacher: TeacherProfile | null }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    whatsapp: teacher?.whatsapp ? formatBrPhone(teacher.whatsapp) : "",
    status: teacher?.status ?? "onboarding",
    companyName: teacher?.companyName ?? "",
    cnpj: "",
    photoUrl: teacher?.photoUrl ?? "",
    acceptsDuo: teacher?.acceptsDuo ?? true,
    maxStudents: String(teacher?.maxStudents ?? 50),
    maxWeeklyHours: String((teacher?.maxWeeklyMinutes ?? 4200) / 60),
    breakMinutes: String(teacher?.breakMinutes ?? 0),
  });
  const m = useMutation({
    mutationFn: () =>
      api.put(`/v1/admin/users/${userId}/teacher-profile`, {
        whatsapp: f.whatsapp,
        status: f.status,
        companyName: f.companyName || null,
        ...(f.cnpj ? { cnpj: f.cnpj } : {}),
        photoUrl: f.photoUrl || null,
        acceptsDuo: f.acceptsDuo,
        maxStudents: Number(f.maxStudents),
        maxWeeklyMinutes: Number(f.maxWeeklyHours) * 60,
        breakMinutes: Number(f.breakMinutes),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teachers"] }),
  });
  return (
    <div className="stack">
      <div className="grid cols-2">
        <TextInput
          label="WhatsApp"
          value={f.whatsapp}
          onChange={(e) => setF({ ...f, whatsapp: e.target.value })}
        />
        <SelectInput
          label="Status no rodízio"
          value={f.status}
          onChange={(e) => setF({ ...f, status: e.target.value as TeacherProfile["status"] })}
          options={[
            { value: "onboarding", label: "Em cadastro" },
            { value: "active", label: "Ativo" },
            { value: "paused", label: "Pausado" },
            { value: "disabled", label: "Desligado" },
          ]}
        />
      </div>
      <div className="grid cols-2">
        <TextInput
          label="Razão social (PJ)"
          value={f.companyName}
          onChange={(e) => setF({ ...f, companyName: e.target.value })}
        />
        <TextInput
          label={teacher?.hasCnpj ? "CNPJ (já cadastrado; preencha para trocar)" : "CNPJ"}
          value={f.cnpj}
          onChange={(e) => setF({ ...f, cnpj: e.target.value })}
        />
      </div>
      <TextInput
        label="Foto (URL)"
        value={f.photoUrl}
        onChange={(e) => setF({ ...f, photoUrl: e.target.value })}
      />
      <div className="grid cols-3">
        <TextInput
          label="Máximo de alunos"
          type="number"
          value={f.maxStudents}
          onChange={(e) => setF({ ...f, maxStudents: e.target.value })}
        />
        <TextInput
          label="Máximo de horas por semana"
          type="number"
          max={70}
          value={f.maxWeeklyHours}
          onChange={(e) => setF({ ...f, maxWeeklyHours: e.target.value })}
        />
        <SelectInput
          label="Intervalo entre aulas"
          value={f.breakMinutes}
          onChange={(e) => setF({ ...f, breakMinutes: e.target.value })}
          options={["0", "10", "15"].map((v) => ({ value: v, label: `${v} min` }))}
        />
      </div>
      <Checkbox
        label="Aceita dupla"
        checked={f.acceptsDuo}
        onChange={(e) => setF({ ...f, acceptsDuo: e.target.checked })}
      />
      {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
      {m.isSuccess ? <Alert kind="success">Dados do professor salvos.</Alert> : null}
      <Button loading={m.isPending} onClick={() => m.mutate()}>
        Salvar
      </Button>
    </div>
  );
}

function WalletForm({
  userId,
  ownerType,
  wallet,
}: {
  userId: string;
  ownerType: "teacher" | "seller";
  wallet: WalletRow | null;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"create" | "link">("create");
  const [f, setF] = useState({
    cpfCnpj: "",
    companyType: "MEI",
    birthDate: "",
    mobilePhone: "",
    incomeValue: "5000",
    street: "",
    number: "",
    complement: "",
    district: "",
    cep: "",
    walletId: "",
    apiKey: "",
  });
  const m = useMutation({
    mutationFn: () =>
      mode === "create"
        ? api.post(`/v1/admin/users/${userId}/wallet`, {
            mode,
            ownerType,
            cpfCnpj: f.cpfCnpj,
            companyType: f.cpfCnpj.replace(/\D/g, "").length === 14 ? f.companyType : undefined,
            birthDate: f.birthDate || undefined,
            mobilePhone: f.mobilePhone,
            incomeValue: Number(f.incomeValue),
            address: {
              street: f.street,
              number: f.number,
              complement: f.complement || undefined,
              district: f.district,
              cep: f.cep,
            },
          })
        : api.post(`/v1/admin/users/${userId}/wallet`, {
            mode,
            ownerType,
            walletId: f.walletId,
            apiKey: f.apiKey || undefined,
          }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wallets"] }),
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <div className="stack">
      {wallet ? (
        <Alert
          kind={wallet.status === "approved" ? "success" : "warning"}
          title={`Subconta ${wallet.status === "approved" ? "aprovada" : "em análise"}`}
        >
          Carteira {wallet.walletId}. O status é atualizado de hora em hora pela Asaas.
        </Alert>
      ) : (
        <Alert kind="note">
          Sem subconta aprovada, o professor não entra no rodízio, e o repasse do professor ou da
          vendedora fica guardado até a aprovação.
        </Alert>
      )}
      <Chips
        value={mode}
        onChange={setMode}
        options={[
          { value: "create", label: "Criar subconta na Asaas" },
          { value: "link", label: "Vincular carteira existente" },
        ]}
      />
      {mode === "create" ? (
        <>
          <div className="grid cols-2">
            <TextInput label="CPF ou CNPJ" value={f.cpfCnpj} onChange={set("cpfCnpj")} />
            <SelectInput
              label="Tipo de empresa"
              value={f.companyType}
              onChange={set("companyType")}
              options={["MEI", "LIMITED", "INDIVIDUAL", "ASSOCIATION"].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>
          <div className="grid cols-3">
            <TextInput label="Celular" value={f.mobilePhone} onChange={set("mobilePhone")} />
            <TextInput
              label="Nascimento (se CPF)"
              type="date"
              value={f.birthDate}
              onChange={set("birthDate")}
            />
            <TextInput
              label="Faturamento mensal (R$)"
              type="number"
              value={f.incomeValue}
              onChange={set("incomeValue")}
            />
          </div>
          <div className="grid cols-3">
            <TextInput label="CEP" value={f.cep} onChange={set("cep")} />
            <TextInput label="Rua" value={f.street} onChange={set("street")} />
            <TextInput label="Número" value={f.number} onChange={set("number")} />
          </div>
          <div className="grid cols-2">
            <TextInput label="Complemento" value={f.complement} onChange={set("complement")} />
            <TextInput label="Bairro" value={f.district} onChange={set("district")} />
          </div>
        </>
      ) : (
        <>
          <TextInput label="Wallet ID" value={f.walletId} onChange={set("walletId")} />
          <TextInput
            label="Chave de API da subconta (opcional, para saldo e status)"
            value={f.apiKey}
            onChange={set("apiKey")}
          />
        </>
      )}
      {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
      {m.isSuccess ? <Alert kind="success">Subconta registrada.</Alert> : null}
      <Button loading={m.isPending} onClick={() => m.mutate()}>
        {mode === "create" ? "Criar subconta" : "Vincular"}
      </Button>
    </div>
  );
}
